import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Worksheet, WorksheetQuery, WorksheetService, WorksheetType } from '@fgb/core';
import { firstValueFrom, Observable } from 'rxjs';

enum PageRequestedType {
  GALLERY = 'Gallery',
  NEWSLETTERS = 'Newsletters',
  TESTIMONIALS = 'Testimonials',
}

@Component({
  selector: 'fgb-worksheet-list',
  templateUrl: './worksheet-list.component.html',
  styleUrls: ['./worksheet-list.component.scss'],
})
export class WorksheetListComponent implements OnInit, OnDestroy {
  @Input() colOverride: string = '';
  @Input() pageRequested: `${PageRequestedType}`;

  worksheetType = WorksheetType;
  pageRequestedType = PageRequestedType;
  isList: boolean = true;
  worksheets$: Observable<Worksheet[]>;

  testimonialPages: number[] = [];
  activeTestimonialPage: number = 0;

  private testimonialsTrackEl?: HTMLElement;
  private resizeObserver?: ResizeObserver;

  constructor(private worksheetService: WorksheetService, private worksheetQuery: WorksheetQuery, private router: Router) {}

  ngOnInit() {
    const currentUrl = this.router.url.replace('/', '').split('?')[0];

    this.worksheets$ = this.worksheetQuery.getWorksheetByRoute(currentUrl);
    if (!this.worksheetQuery.hasRoute(currentUrl)) {
      firstValueFrom(this.worksheetService.fetchWorksheetByRoute(currentUrl));
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /** The track sits inside an async block, so it is picked up whenever it enters or leaves the view. */
  @ViewChild('testimonialsTrack')
  set testimonialsTrack(ref: ElementRef<HTMLElement> | undefined) {
    this.resizeObserver?.disconnect();
    this.testimonialsTrackEl = ref?.nativeElement;

    if (this.testimonialsTrackEl) {
      this.resizeObserver = new ResizeObserver(() => this.updateTestimonialPages());
      this.resizeObserver.observe(this.testimonialsTrackEl);
      this.updateTestimonialPages();
    }
  }

  /** Keeps the homepage from rendering an empty testimonials band when no worksheet is configured. */
  hasWorksheetType(worksheets: Worksheet[], worksheetType: WorksheetType): boolean {
    return worksheets.some((group) => group.Items?.some((item) => item.WorksheetType === worksheetType));
  }

  onTestimonialsScroll(): void {
    this.updateTestimonialPages();
  }

  goToTestimonialPage(index: number): void {
    const el = this.testimonialsTrackEl;
    if (el) {
      el.scrollTo({ left: index * this.testimonialCardStride(el), behavior: 'smooth' });
    }
  }

  /** 0 = active, 1 = neighbour, 2 = further away. Drives the 8px / 6px / 4px dot sizing from the design. */
  testimonialDotDistance(index: number): number {
    return Math.min(Math.abs(index - this.activeTestimonialPage), 2);
  }

  /**
   * A dot per card the track can scroll to, measured from the track itself rather than a breakpoint,
   * so the dots disappear whenever every card already fits on screen (three across on desktop, one on mobile).
   * Counting by card rather than by viewport is what makes a fourth testimonial show a second dot.
   */
  private updateTestimonialPages(): void {
    const el = this.testimonialsTrackEl;
    if (!el || !el.clientWidth) {
      return;
    }

    const stride = this.testimonialCardStride(el);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const pageCount = stride > 0 && maxScroll > 1 ? Math.round(maxScroll / stride) + 1 : 1;

    this.testimonialPages = Array.from({ length: pageCount }, (_, index) => index);
    this.activeTestimonialPage = stride > 0 ? Math.min(pageCount - 1, Math.round(el.scrollLeft / stride)) : 0;
  }

  /** Distance from one card to the next, so the gap and the responsive card width are both accounted for. */
  private testimonialCardStride(el: HTMLElement): number {
    const cards = el.querySelectorAll<HTMLElement>('.testimonial-card');
    if (cards.length > 1) {
      return cards[1].offsetLeft - cards[0].offsetLeft;
    }

    return cards.length ? cards[0].offsetWidth : 0;
  }
}
